import type { Task } from '../../types';

import { BuildSteps } from './constants';
import type { BuildStep } from './constants';

/**
 * TASK_PICKED_UP_BY_WORKER_TIMEOUT_MS is the amount of time we wait before
 * deciding that a task has not been picked up by a worker and is unlikely to
 * be completed. This matches how we query Tasks to manually fail within the
 * "TaskWatchdog / Timeout" job. Ideally, this should stay in sync with the
 * START_TIME_TIMEOUT constant defined there.
 */
export const TASK_PICKED_UP_BY_WORKER_TIMEOUT_MS = 30 * 60 * 1000;

function taskHasNoMoreRetries(task: Task): boolean {
  if (!task.attempt_number || !task.maxRetries) {
    return true;
  }
  return task.attempt_number > task.maxRetries;
}

function taskWasNeverPickedUpByWorker(task: Task): boolean {
  // If there is no scheduledStartTime, we can't confidently know whether the task was
  // never picked up by a worker (because we don't know _when_ it should've been picked up).
  if (!task.scheduledStartTime) {
    return false;
  }

  const now = new Date();
  const scheduledStartTime = new Date(task.scheduledStartTime);
  return (now.getTime() - scheduledStartTime.getTime()) > TASK_PICKED_UP_BY_WORKER_TIMEOUT_MS;
}

const getBuildStep = (task?: Task, isSteady?: boolean): BuildStep => {
  if (!task || task.status === 'CREATED') {
    return BuildSteps.Queued;
  }

  if (task.status === 'FAILED' && (taskHasNoMoreRetries(task) || taskWasNeverPickedUpByWorker(task))) {
    return BuildSteps.Failed;
  }
  if (task.status === 'SUCCEEDED' && (isSteady ?? true)) {
    return BuildSteps.Succeeded;
  }
  if (task.status === 'SUCCEEDED' && !isSteady) {
    return BuildSteps.AnalyzingDeps;
  }

  return BuildSteps.ApplyingBuild;
};

// hasTaskStarted returns whether the task has gotten past the CREATED or ASSIGNED states.
// If task is falsy this function returns false.
const hasTaskStarted = (task?: Pick<Task, 'status'>): boolean =>
  !!task && task.status !== 'CREATED' && task.status !== 'ASSIGNED';

const codeSentryTasks = new Set(['CodeSentryDataSync', 'BinaryDecompositionBuild']);

// isTaskFinished returns whether the scan has finished (whether successfully or not).
// If task is falsy this function returns false.
const isTaskFinished = (task?: Task): boolean => {
  if (!task) {
    return false;
  }
  const isTaskFailed = task?.status === 'FAILED' && (taskHasNoMoreRetries(task) || taskWasNeverPickedUpByWorker(task));
  // CodeSentry integration involves multiple tasks.  If the task is a CodeSentry task and it has not failed,
  // then the task is not finished and we should continue polling.
  if (codeSentryTasks.has(task.task) && !isTaskFailed) {
    return false;
  }

  return (
    task.status === 'SUCCEEDED' || isTaskFailed
  );
};

const taskFns = {
  getBuildStep,
  hasTaskStarted,
  isTaskFinished,
  taskHasNoMoreRetries,
  taskWasNeverPickedUpByWorker,
};

export default taskFns;
